import React, { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../configs/store';
import Form from '../../Form/Form';
import AppModeContext from 'src/containers/AppMode/AppMode.context';

interface RegisterProps {
  close: () => void,
  countries: [],
  provinces: [],
  registered: boolean,
  passwordStrength: { minLength: number, maxLength: number, minStrength: number }
}

function Register({ close }: RegisterProps) {
  const { t } = useTranslation('common');
  const history = useHistory();
  const { registered } = useSelector((state: RootState) => state.auth);
  const { countries, provinces } = useSelector((state: RootState) => state.odds);
  const { passwordStrength } = useSelector((state: RootState) => state.content);
  const retailStrength = {
    min: 4,
    max: 6,
    strength: 1
  };
  const dispatch = useDispatch();
  const { RETAIL_MODE } = useContext(AppModeContext);
  const isRetail = RETAIL_MODE;
  useEffect(() => {
    // If just registered, redirect to login modal.
    if (registered) {
      dispatch.auth.setRegister(false);
      history.push('#register-confirmation');
      close();
    }
  }, [registered]); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = [
    {
      label: t('form.personalLabel'),
      inputs: [
        {
          type: 'select',
          label: t('form.titleLabel'),
          name: 'title',
          items: [
            { value: '1', text: t('form.mr') },
            { value: '2', text: t('form.mrs') },
            { value: '3', text: t('form.miss') }
          ]
        },
        {
          type: 'text',
          label: t('form.idNumberLabel'),
          placeholder: t('form.idNumberPlaceholder'),
          name: 'idNumber',
          rules: {
            required: !isRetail
          }
        },
        {
          type: 'text',
          label: t('form.firstNameLabel'),
          placeholder: t('form.firstNamePlaceholder'),
          name: 'firstname',
          rules: {
            required: !isRetail
          }
        },
        {
          type: 'text',
          label: t('form.lastNameLabel'),
          placeholder: t('form.lastNamePlaceholder'),
          name: 'lastname',
          rules: {
            required: !isRetail
          }
        },
        {
          type: 'date',
          label: t('form.birthdateLabel'),
          className: 'full-width',
          placeholder: t('form.birthdatePlaceholder'),
          name: 'birthdate',
          rules: {
            required: !isRetail,
            pattern: !isRetail ? /^(((19|[2-9]\d)\d{2})\-(0[13578]|1[02])\-(0[1-9]|[12]\d|3[01]))|(((19|[2-9]\d)\d{2})\-(0[13456789]|1[012])\-(0[1-9]|[12]\d|30))|(((19|[2-9]\d)\d{2})\-02\-(0[1-9]|1\d|2[0-8]))|(((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|(([1][26]|[2468][048]|[3579][26])00))\-02\-29)$/ : '', // eslint-disable-line
            over: !isRetail ? 18 : 0
          }
        },
        {
          type: 'phone',
          label: t('form.phoneNumberLabel'),
          placeholder: t('form.phoneNumberPlaceholder'),
          name: 'phoneNumber',
          rules: {
            required: !isRetail,
            pattern: /^(\+)[1-9][0-9 \-\(\)\.]{7,32}$/,
            validate: 'phone'
          }
        },
        {
          type: 'phone',
          label: t('form.phoneNumberConfirmation'),
          placeholder: t('form.phoneNumberPlaceholder'),
          name: 'phoneNumberConfirmation',
          rules: {
            required: !isRetail,
            pattern: /^(\+)[1-9][0-9 \-\(\)\.]{7,32}$/,
            match: 'phoneNumber'
          }
        },
        {
          type: 'email',
          label: t('form.emailLabel'),
          placeholder: t('form.emailPlaceholder'),
          name: 'email',
          rules: {
            isRetail: isRetail,
            required: !isRetail,
            validate: 'email',
            pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line
          }
        },
        {
          type: 'email',
          label: t('form.confirmEmailLabel'),
          placeholder: t('form.confirmEmailPlaceholder'),
          name: 'confirmEmail',
          rules: {
            isRetail: isRetail,
            validate: 'email',
            pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ , // eslint-disable-line
            match: 'email'
          }
        },
        {
          type: 'text',
          className: 'full-width',
          label: t('form.streetLabel'),
          placeholder: t('form.streetPlaceholder'),
          name: 'street',
          rules: {
            required: !isRetail
          }
        },
        {
          hidden: true
        },
        {
          type: 'text',
          label: t('form.postalCodeLabel'),
          placeholder: t('form.postalCodePlaceholder'),
          name: 'postalCode',
          rules: {
            required: !isRetail
          }
        },
        {
          type: 'text',
          label: t('form.cityLabel'),
          placeholder: t('form.cityPlaceholder'),
          name: 'city',
          rules: {
            required: !isRetail
          }
        },
        {
          type: 'select',
          label: t('form.countryLabel'),
          placeholder: t('form.countryPlaceholder'),
          name: 'country',
          items: countries,
          onSelect: 'getProvinces',
          rules: {
            required: !isRetail
          }
        },
        {
          type: 'select',
          label: t('form.provinceLabel'),
          placeholder: t('form.provincePlaceholder'),
          name: 'province',
          items: provinces,
          rules: {
            dependency: 'country'
          }
        }
      ],
      buttons: [
        {
          type: 'arrow',
          label: t('form.nextButton'),
          action: 'next'
        }
      ],
      links: [
        {
          text: t('form.registerLogin'),
          action: 'login',
          hasButton: true
        }
      ],
      span: [t('form.indicatesRequiredField')]
    },
    {
      label: t('form.accountInfoLabel'),
      inputs: [
        {
          type: 'password',
          label: isRetail ? t('form.retailPasswordLabel') : t('form.passwordLabel'),
          name: 'password',
          isRetail: isRetail,
          placeholder: t('form.passwordHolder'),
          rules: {
            required: true,
            strength: isRetail ? retailStrength.strength : passwordStrength.minStrength,
            min: isRetail ? retailStrength.min : passwordStrength.minLength,
            max:  isRetail ? retailStrength.max : passwordStrength.maxLength
          }
        },
        {
          type: 'password',
          label: t('form.confirmPasswordLabel'),
          name: 'confirmPassword',
          placeholder: t('form.passwordHolder'),
          rules: {
            required: true,
            match: 'password'
          }
        },
        {
          ...(isRetail && {
            type: 'text',
            label: t('form.cardsNumber'),
            name: 'cardNumber',
            placeholder: '1234',
            rules: {
              required: false
            }
          })
        },
        {
          type: 'select',
          label: t('form.securityQuestionLabel'),
          name: 'securityQuestion',
          placeholder: t('form.question1'),
          className: 'full-width',
          items: [
            { value: 1, text: t('form.question1') },
            { value: 2, text: t('form.question2') },
            { value: 3, text: t('form.question3') },
            { value: 4, text: t('form.question4') },
            { value: 5, text: t('form.question5') },
            { value: 6, text: t('form.question6') },
            { value: 7, text: t('form.question7') },
            { value: 8, text: t('form.question8') },
            { value: 9, text: t('form.question9') },
            { value: 10, text: t('form.question10') },
            { value: 11, text: t('form.question11') },
            { value: 12, text: t('form.question12') },
            { value: 13, text: t('form.question13') },
            { value: 14, text: t('form.question14') },
            { value: 15, text: t('form.question15') },
            { value: 16, text: t('form.question16') },
            { value: 17, text: t('form.question17') },
            { value: 18, text: t('form.question18') },
            { value: 19, text: t('form.question19') }
          ],
          rules: {
            required: !isRetail
          }
        },
        {
          hidden: true
        },
        {
          type: 'text',
          label: t('form.answer'),
          name: 'answer',
          className: 'full-width',
          placeholder: 'Your Answer',
          rules: {
            required: !isRetail
          }
        },
        {
          hidden: true
        }
      ],
      buttons: [
        {
          type: 'back-arrow',
          label: t('form.nextButton'),
          action: 'prev'
        },
        {
          type: 'arrow',
          label: t('form.nextButton'),
          action: 'next'
        }
      ],
      links: [
        {
          text: t('form.registerLogin'),
          action: 'login',
          hasButton: true
        }
      ],
      span: [t('form.indicatesRequiredField')]
    },
    {
      label: t('form.confirmationLabel'),
      inputs: [
        {
          type: 'checkbox',
          label: t('form.specialOffersLabel'),
          name: 'subscribeProducts'
        },
        {
          type: 'checkbox',
          label: t('form.over18Label'),
          name: 'over18',
          link: '/static/terms-and-conditions',
          rules: {
            required: true
          }
        },
        {
          type: 'recaptcha'
        }
      ],
      buttons: [
        {
          type: 'submit',
          className: 'full-width modal-submit',
          label: t('form.registerButton')
        },
        {
          type: 'back-arrow',
          label: t('form.nextButton'),
          action: 'prev'
        }
      ],
      links: [
        {
          text: t('form.registerLogin'),
          action: 'login',
          hasButton: true
        }
      ],
      span: [t('form.indicatesRequiredField')]
    }
  ];

  return (
    <Form
      containerClass={ !isRetail ? 'register-form' : 'retailRegister-form' }
      steps={ steps }
      close={ close }
    />
  );
}

export default Register;
